import * as React from 'react';
import { css, html } from 'react-strict-dom';
import { useForm } from 'react-typed-form';

import { Api, useApiFormSubmit } from '../../api';
import { MarkdownText, Text } from '../../components';
import { FormWrap, SubmitButton } from '../../form';

type Props = {
  release: Api.Release.V;
  invalidate: () => unknown;
};

export default function SectionReleaseNotes({ release, invalidate }: Props) {
  const [editing, setEditing] = React.useState(false);

  const onSubmit = useApiFormSubmit('PUT', `/releases/${release.id}/client`, {
    invalidate,
  });
  const form = useForm<Api.Release.W_Update_partial>({
    defaultValues: { releaseNotes: release.releaseNotes },
    onSubmit,
  });

  const needsReleaseNotes = release.builds.some((b) => b.platform === 'ios');
  if (!needsReleaseNotes) return null;

  const startedRelease = release.builds.some(
    (b) => b.platform === 'ios' && b.buildEvents.length
  );

  const releaseNotesField = form.getField('releaseNotes');
  return (
    <html.div>
      <Text as="h3" style={styles.sectionHeading}>
        Release notes
        {!startedRelease && (
          <html.a
            style={styles.promptButton}
            onClick={() => {
              setEditing(!editing);
              form.reset();
            }}
          >
            <i className="fa-regular fa-pen-to-square" />
          </html.a>
        )}
      </Text>
      {editing ? (
        <FormWrap {...form}>
          <html.textarea
            value={releaseNotesField.value || ''}
            onChange={(ev: React.ChangeEvent<HTMLTextAreaElement>) =>
              releaseNotesField.handleValueChange(ev.target.value)
            }
            disabled={releaseNotesField.isLoading}
            style={styles.textarea}
          />
          <SubmitButton {...form} label="Submit" />
        </FormWrap>
      ) : (
        <MarkdownText
          body={release.releaseNotes}
          emptyText="Provide release notes to be shown to users in the App Store"
        />
      )}
    </html.div>
  );
}

const styles = css.create({
  sectionHeading: {
    marginBlockStart: 25,
    marginBlockEnd: 15,
    color: '#1C1D20',
    fontSize: 18,
    fontWeight: '600',
  },
  promptButton: {
    color: '#ccd',
    cursor: 'pointer',
    fontSize: 16,
    transition: 'color 0.2s',
    marginLeft: 10,
    ':hover': {
      color: '#0366d6',
    },
  },
  textarea: {
    width: '100%',
    height: 200,
    fontFamily: 'Inter, sans-serif',
    fontSize: 15,
    lineHeight: 1.5,
  },
});
